import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar, Grid, Divider, Card
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-hot-toast';
import config, { getApiUrl, getEndpoint } from '../config';

const API_URL = getEndpoint('LANDING');
const MAX_IMAGE_SIZE = config.FILE_LIMITS.IMAGE;
const MAX_VIDEO_SIZE = config.FILE_LIMITS.VIDEO;

const Landing = () => {
  const [landing, setLanding] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    banner: { title: '', subtitle: '', image: '', video: '' },
    about: { text: '', image: '' },
    certificates: [],
    services: { image: '', items: [] },
    software_team: [],
    experience: [],
    cta: { title: '', phone: '', email: '' },
    faq:{image:''},
    seo:{
      meta_keywords:'',
      meta_title:'',
      meta_description:''
    }
  });
  const [files, setFiles] = useState({});

  useEffect(() => {
    fetchLanding();
  }, []);

  const fetchLanding = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('توکن احراز هویت یافت نشد. لطفاً دوباره وارد شوید.');
        window.location.href = '/login';
        return;
      }
      
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data) {
        setLanding(res.data)
        
        // اگر data وجود دارد و رشته JSON معتبر است، آن را parse کن
        if (res.data.data && typeof res.data.data === 'string') {
          try {
            const parsedData = JSON.parse(res.data.data);
            setForm({ ...form, ...parsedData });
          } catch (parseError) {
            console.error('Error parsing landing data:', parseError);
            toast.error('خطا در پردازش اطلاعات لندینگ');
          }
        } else if (res.data.data && typeof res.data.data === 'object') {
          // اگر data از قبل object است
          setForm({ ...form, ...res.data.data });
        }
        // اگر data وجود ندارد، فرم خالی باقی می‌ماند (برای ایجاد لندینگ جدید)
      }
    } catch (err) {
      console.error('Error fetching landing:', err);
      if (err.response && err.response.status === 401) {
        toast.error('توکن منقضی شده است. لطفاً دوباره وارد شوید.');
        localStorage.removeItem('token');
        window.location.href = '#/login';
      } else if (err.response && err.response.status === 403) {
        toast.error('توکن نامعتبر است. لطفاً دوباره وارد شوید.');
        localStorage.removeItem('token');
        window.location.href = '#/login';
      } else {
        toast.error('خطا در دریافت اطلاعات لندینگ');
      }
    }
    setLoading(false);
  };

  const handleInput = (section, key, value) => {
    setForm(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  const handleFile = (section, key, file) => {
    if (!file) return;
    if (key === 'video' && file.size > MAX_VIDEO_SIZE) {
      toast.error('حجم ویدیو نباید بیشتر از ۳ مگابایت باشد');
      return;
    }
    if (key !== 'video' && file.size > MAX_IMAGE_SIZE) {
      toast.error('حجم تصویر نباید بیشتر از ۲۰۰ کیلوبایت باشد');
      return;
    }
    setFiles(prev => ({ ...prev, [`${section}_${key}`]: file }));
    setForm(prev => ({ ...prev, [section]: { ...prev[section], [key]: URL.createObjectURL(file) } }));
  };

  // Helper functions for list management
  const handleAddItem = (section) => {
    const newItem = {};
    switch (section) {
      case 'certificates':
        newItem.title = '';
        newItem.image = '';
        break;
      case 'services':
        newItem.title = '';
        newItem.description = '';
        break;
      case 'software_team':
        newItem.name = '';
        newItem.position = '';
        newItem.image = '';
        break;
      case 'experience':
        newItem.title = '';
        newItem.company = '';
        newItem.period = '';
        newItem.description = '';
        break;
      default:
        break;
    }
    
    if (section === 'services') {
      setForm(prev => ({ 
        ...prev, 
        services: { 
          ...prev.services, 
          items: [...(prev.services.items || []), newItem] 
        } 
      }));
    } else {
      setForm(prev => ({ ...prev, [section]: [...(prev[section] || []), newItem] }));
    }
  };

  const handleRemoveItem = (section, index) => {
    if (section === 'services') {
      setForm(prev => ({ 
        ...prev, 
        services: { 
          ...prev.services, 
          items: prev.services.items.filter((_, i) => i !== index) 
        } 
      }));
    } else {
      setForm(prev => ({ ...prev, [section]: prev[section].filter((_, i) => i !== index) }));
    }
  };

  const handleListItemChange = (section, index, key, value) => {
    if (section === 'services') {
      setForm(prev => ({
        ...prev,
        services: {
          ...prev.services,
          items: prev.services.items.map((item, i) => i === index ? { ...item, [key]: value } : item)
        }
      }));
    } else {
      setForm(prev => ({
        ...prev,
        [section]: prev[section].map((item, i) => i === index ? { ...item, [key]: value } : item)
      }));
    }
  };

  const handleListItemFile = (section, index, key, file) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('حجم تصویر نباید بیشتر از ۲۰۰ کیلوبایت باشد');
      return;
    }
    const fileUrl = URL.createObjectURL(file);
    setFiles(prev => ({ ...prev, [`${section}_${index}_${key}`]: file }));
    setForm(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => i === index ? { ...item, [key]: fileUrl } : item)
    }));
  };

  const handleSubmit = async () => {
    const formData = new FormData();
  
    // Attach files (با همان نام کلید)
    Object.entries(files).forEach(([k, v]) => {
      formData.append(k, v);
    });
  
    // Attach JSON data
    const dataToSend = { ...form };
  
    // Remove file URLs from data
    Object.keys(files).forEach(k => {
      const parts = k.split('_'); 
      if (parts.length === 2) {
        const [section, key] = parts;
        if (dataToSend[section]) dataToSend[section][key] = '';
      } else if (parts.length === 4) {
        const [section, sub, index, key] = parts;
        const sectionName = `${section}_${sub}`;
        if (dataToSend[sectionName] && dataToSend[sectionName][index]) {
          dataToSend[sectionName][index][key] = '';
        }
      }
    });
  
    formData.append('data', JSON.stringify(dataToSend));
  
    
  
    try {
      const token = localStorage.getItem('token');
      if (landing && landing.id) {
        await axios.put(`${API_URL}/${landing.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('لندینگ بروزرسانی شد');
      } else {
        await axios.post(API_URL, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('لندینگ ثبت شد');
      }
      fetchLanding();
    } catch (err) {
      console.error(err);
      toast.error('خطا در ذخیره لندینگ');
    }
  };
  

  // Render helpers for list sections (certificates, services, software_team, experience)
  // ... (for brevity, not included in this snippet, but would include add/edit/delete for each list)

  return (
    <Box sx={{ fontFamily: 'Vazir', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontFamily: 'Vazir', fontWeight: 'bold' }}>مدیریت لندینگ پیج</Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <TextField
              value={form.seo.meta_title}
                label="Meta Title"
                fullWidth
                onChange={e => handleInput('seo', 'meta_title', e.target.value)}
                sx={{ fontFamily: 'Vazir' }}
                inputProps={{ style: { fontFamily: 'Vazir' } }}
              />
              <TextField
                label="Meta Description"
                value={form.seo.meta_description}
                fullWidth
                multiline
                minRows={3}
                onChange={e => handleInput('seo', 'meta_description', e.target.value)}
               
                sx={{ fontFamily: 'Vazir' }}
                inputProps={{ style: { fontFamily: 'Vazir' } }}
              />
              <TextField
                label="Meta Keywords"
                value={form.seo.meta_keywords}
                fullWidth
                onChange={e => handleInput('seo', 'meta_keywords', e.target.value)}
               
                sx={{ fontFamily: 'Vazir' }}
                inputProps={{ style: { fontFamily: 'Vazir' } }}
              />
            </Box>
      {/* Banner Section */}
      <Box sx={{ mb: 3, bgcolor: '#f9f9f9', p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir', mb: 1 }}>بنر</Typography>
        <TextField label="تیتر اول" fullWidth value={form.banner.title} onChange={e => handleInput('banner', 'title', e.target.value)} sx={{ fontFamily: 'Vazir',marginY:"20px" }} />
        <TextField label="زیرتیتر اول" fullWidth value={form.banner.subtitle} onChange={e => handleInput('banner', 'subtitle', e.target.value)} sx={{ fontFamily: 'Vazir' }} />

        <Grid container spacing={5}>
          
          
          <Grid item xs={12} md={6}>
            <Button variant="outlined" component="label" sx={{ fontFamily: 'Vazir', mt: 1 }}>
              انتخاب تصویر بنر (حداکثر ۲۰۰ کیلوبایت)
              <input type="file" accept="image/*" hidden onChange={e => handleFile('banner', 'image', e.target.files[0])} />
            </Button>
            {form.banner.image && <Avatar height={400}  src={getApiUrl(`/api/media/download?path=${form.banner.image.split('/').pop()}`)} sx={{ width: 200, height: 200, mt: 1 }} />}
          </Grid>
          <Grid item xs={12} md={6}>
            <Button variant="outlined" component="label" sx={{ fontFamily: 'Vazir', mt: 1 }}>
              انتخاب ویدیو (حداکثر ۳ مگابایت)
              <input type="file" accept="video/*" hidden onChange={e => handleFile('banner', 'video', e.target.files[0])} />
            </Button>
            {form.banner.video && <video src={getApiUrl(`/api/media/download?path=${form.banner.video.split('/').pop()}`)} controls style={{ height:200 , marginTop: 8,display:"block" }} />}
          </Grid>
        </Grid>
      </Box>
      {/* About Section */}
      <Box sx={{ mb: 3, bgcolor: '#f9f9f9', p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir', mb: 1 }}>درباره من</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography variant="body2" sx={{ fontFamily: 'Vazir', mb: 1, color: 'text.secondary' }}>متن درباره من:</Typography>
            <Box sx={{ border: '1px solid #ddd', borderRadius: 1 }}>
              <ReactQuill
                theme="snow"
                value={form.about.text}
                onChange={(content) => handleInput('about', 'text', content)}
                style={{ height: '200px', fontFamily: 'Vazir' }}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'align': [] }],
                    ['link'],
                    ['clean']
                  ]
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button variant="outlined" component="label" sx={{ fontFamily: 'Vazir', mt: 1 }}>
              انتخاب تصویر درباره من (حداکثر ۲۰۰ کیلوبایت)
              <input type="file" accept="image/*" hidden onChange={e => handleFile('about', 'image', e.target.files[0])} />
            </Button>
            {form.about.image && <Avatar src={getApiUrl(`/api/media/download?path=${form.about.image.split('/').pop()}`)} sx={{ width: 200, height: 200, mt: 1 }} />}
          </Grid>
        </Grid>
      </Box>
      {/* Certificates Section */}
      <Box sx={{ mb: 3, bgcolor: '#f9f9f9', p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir', mb: 1 }}>افتخارات</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleAddItem('certificates')} sx={{ mb: 2, fontFamily: 'Vazir' }}>
          افزودن افتخار
        </Button>
        <Grid container spacing={2}>
          {form.certificates.map((cert, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ p: 2 }}>
                <TextField label="عنوان" fullWidth value={cert.title || ''} onChange={e => handleListItemChange('certificates', index, 'title', e.target.value)} sx={{ fontFamily: 'Vazir', mb: 1 }} />
                 <Button variant="outlined" component="label" sx={{ fontFamily: 'Vazir', mb: 1 }}>
                  انتخاب تصویر
                  <input type="file" accept="image/*" hidden onChange={e => handleListItemFile('certificates', index, 'image', e.target.files[0])} />
                </Button>
                {cert.image && <Avatar src={getApiUrl(`/api/media/download?path=${cert.image.split('/').pop()}`)} sx={{ width: 60, height: 60, mb: 1 }} />}
                <IconButton color="error" onClick={() => handleRemoveItem('certificates', index)}><DeleteIcon /></IconButton>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Services Section */}
      <Box sx={{ mb: 3, bgcolor: '#f9f9f9', p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir', mb: 1 }}>خدمات</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Button variant="outlined" component="label" sx={{ fontFamily: 'Vazir' }}>
              انتخاب تصویر کلی بخش خدمات (حداکثر ۲۰۰ کیلوبایت)
              <input type="file" accept="image/*" hidden onChange={e => handleFile('services', 'image', e.target.files[0])} />
            </Button>
            {form.services.image && <Avatar src={getApiUrl(`/api/media/download?path=${form.services.image.split('/').pop()}`)} sx={{ width: 200, height: 200, mt: 1 }} />}
          </Grid>
        </Grid>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleAddItem('services')} sx={{ mb: 2, fontFamily: 'Vazir' }}>
          افزودن خدمت
        </Button>
        <Grid container spacing={2}>
          {form.services.items && form.services.items.map((service, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ p: 2 }}>
                <TextField label="عنوان" fullWidth value={service.title || ''} onChange={e => handleListItemChange('services', index, 'title', e.target.value)} sx={{ fontFamily: 'Vazir', mb: 1 }} />
                <TextField label="توضیحات" fullWidth multiline minRows={2} value={service.description || ''} onChange={e => handleListItemChange('services', index, 'description', e.target.value)} sx={{ fontFamily: 'Vazir', mb: 1 }} />
                <TextField label="توضیحات تکمیلی" fullWidth multiline minRows={3} value={service.breif || ''} onChange={e => handleListItemChange('services', index, 'breif', e.target.value)} sx={{ fontFamily: 'Vazir', mb: 1 }} />
                <IconButton color="error" onClick={() => handleRemoveItem('services', index)}><DeleteIcon /></IconButton>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Software Team Section */}
      <Box sx={{ mb: 3, bgcolor: '#f9f9f9', p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir', mb: 1 }}>عکس های پیشرفت</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleAddItem('software_team')} sx={{ mb: 2, fontFamily: 'Vazir' }}>
          افزودن عکس
        </Button>
        <Grid container spacing={2}>
          {form.software_team.map((member, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ p: 2 }}>
                <TextField label="نام" fullWidth value={member.name || ''} onChange={e => handleListItemChange('software_team', index, 'name', e.target.value)} sx={{ fontFamily: 'Vazir', mb: 1 }} />
                <TextField label="سمت" fullWidth value={member.position || ''} onChange={e => handleListItemChange('software_team', index, 'position', e.target.value)} sx={{ fontFamily: 'Vazir', mb: 1 }} />
                <Button variant="outlined" component="label" sx={{ fontFamily: 'Vazir', mb: 1 }}>
                  انتخاب تصویر
                  <input type="file" accept="image/*" hidden onChange={e => handleListItemFile('software_team', index, 'image', e.target.files[0])} />
                </Button>
                {member.image && <Avatar src={getApiUrl(`/api/media/download?path=${member.image.split('/').pop()}`)} sx={{ width: 60, height: 60, mb: 1 }} />}
                <IconButton color="error" onClick={() => handleRemoveItem('software_team', index)}><DeleteIcon /></IconButton>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Experience Section */}
      <Box sx={{ mb: 3, bgcolor: '#f9f9f9', p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir', mb: 1 }}>تجربیات و تحصیلات</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleAddItem('experience')} sx={{ mb: 2, fontFamily: 'Vazir' }}>
          افزودن 
        </Button>
        <Grid container spacing={2}>
          {form.experience.map((exp, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ p: 2 }}>
                <TextField label="عنوان" fullWidth value={exp.title || ''} onChange={e => handleListItemChange('experience', index, 'title', e.target.value)} sx={{ fontFamily: 'Vazir', mb: 1 }} />
                <TextField label="شرکت" fullWidth value={exp.company || ''} onChange={e => handleListItemChange('experience', index, 'company', e.target.value)} sx={{ fontFamily: 'Vazir', mb: 1 }} />
                <TextField label="دوره زمانی" fullWidth value={exp.period || ''} onChange={e => handleListItemChange('experience', index, 'period', e.target.value)} sx={{ fontFamily: 'Vazir', mb: 1 }} />
                <TextField label="توضیحات" fullWidth multiline minRows={2} value={exp.description || ''} onChange={e => handleListItemChange('experience', index, 'description', e.target.value)} sx={{ fontFamily: 'Vazir', mb: 1 }} />
                <IconButton color="error" onClick={() => handleRemoveItem('experience', index)}><DeleteIcon /></IconButton>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Button variant="outlined" component="label" sx={{ fontFamily: 'Vazir' }}>
              انتخاب تصویر کلی بخش سوالات متداول (حداکثر ۲۰۰ کیلوبایت)
              <input type="file" accept="image/*" hidden onChange={e => handleFile('faq', 'image', e.target.files[0])} />
            </Button>
            {form.faq.image && <Avatar src={getApiUrl(`/api/media/download?path=${form.faq.image.split('/').pop()}`)} sx={{ width: 200, height: 200, mt: 1 }} />}
          </Grid>
        </Grid>
      </Box>

      {/* CTA Section */}
      <Box sx={{ mb: 3, bgcolor: '#f9f9f9', p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir', mb: 1 }}>دکمه تماس (CTA)</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField label="عنوان" fullWidth value={form.cta.title} onChange={e => handleInput('cta', 'title', e.target.value)} sx={{ fontFamily: 'Vazir' }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="شماره تماس" fullWidth value={form.cta.phone} onChange={e => handleInput('cta', 'phone', e.target.value)} sx={{ fontFamily: 'Vazir' }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="ایمیل" fullWidth value={form.cta.email} onChange={e => handleInput('cta', 'email', e.target.value)} sx={{ fontFamily: 'Vazir' }} />
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ my: 3 }} />
      <Button variant="contained" color="primary" onClick={handleSubmit} sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>
        ذخیره لندینگ پیج
      </Button>
    </Box>
  );
};

export default Landing; 